import React, { useState } from 'react';
import SEO from '../components/SEO';

const PrivacyPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('introduction');

  const sections = [
    { id: 'introduction', label: '1. Introduction' },
    { id: 'information-collect', label: '2. Information We Collect' },
    { id: 'how-we-use', label: '3. How We Use Information' },
    { id: 'information-sharing', label: '4. Information Sharing' },
    { id: 'data-security', label: '5. Data Security' },
    { id: 'user-rights', label: '6. Your Rights & Choices' },
    { id: 'cookies', label: '7. Cookies & Tracking' },
    { id: 'changes', label: '8. Changes to This Policy' },
    { id: 'contact', label: '9. Contact Us' }
  ];

  const handleSectionClick = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Offset for fixed header
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO 
        title="Privacy Policy - AlmeOne"
        description="Learn how AlmeOne collects, uses, and protects your personal data in connection with our AI-driven solutions and warehouse automation services."
        keywords="privacy policy, data protection, privacy guidelines, AlmeOne privacy, Qatar data privacy"
      />

      {/* Page Header */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-gold/20 py-20 text-white">
        <div className="max-w-6xl mx-auto px-5 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            <span className="text-gold">Privacy</span> Policy
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Last updated: June 17, 2026. Your privacy and data security are fundamental to how we build technology and serve our clients.
          </p>
        </div>
      </section>

      {/* Main Content Layout */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sticky Sidebar Navigation */}
          <aside className="lg:w-1/4 lg:sticky lg:top-28 h-fit bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
            <h2 className="text-black font-bold text-lg mb-6 border-b border-gray-100 pb-3 flex items-center gap-2">
              <span className="text-gold text-xl">📄</span> Table of Contents
            </h2>
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => handleSectionClick(section.id)}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                    activeSection === section.id
                      ? 'bg-gold/10 text-gold border-l-4 border-gold pl-3'
                      : 'text-gray-600 hover:text-gold hover:bg-gray-50 pl-4'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Privacy Policy Text */}
          <div className="lg:w-3/4 bg-white p-8 sm:p-12 rounded-2xl shadow-lg border border-gray-100 prose prose-lg prose-gold max-w-none text-gray-700 leading-relaxed space-y-12">
            <div id="introduction" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                1. Introduction
              </h2>
              <p>
                AlmeOne ("we," "our," or "us") is dedicated to protecting the privacy and security of your personal data. This Privacy Policy details how we collect, process, share, and protect information when you visit our website, utilize our AI-driven software solutions, participate in our warehouse automation services, or communicate with us.
              </p>
              <p>
                By accessing our services, you consent to the data collection and usage practices described in this policy. If you do not agree with any terms in this Privacy Policy, please discontinue the use of our services.
              </p>
            </div>

            <div id="information-collect" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                2. Information We Collect
              </h2>
              <p>
                We collect information that you directly provide to us, information collected automatically, and information from third parties:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>
                  <strong className="text-black">Personal Identifiers:</strong> Name, business email, telephone number, job title, company name, and physical address provided during inquiry or partnership registration.
                </li>
                <li>
                  <strong className="text-black">Operational Data:</strong> Logistics, warehouse layout descriptions, or data logs necessary for setting up AI-based solutions (e.g., container damage inspection, automated dimensioning data).
                </li>
                <li>
                  <strong className="text-black">Technical Information:</strong> IP address, browser type, operating system, and web navigation behaviors collected via cookies and log files when you visit our website.
                </li>
                <li>
                  <strong className="text-black">Recaptcha Data:</strong> Verification information processed by Google ReCaptcha to protect our contact and inquiry forms against spam and abuse.
                </li>
              </ul>
            </div>

            <div id="how-we-use" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                3. How We Use Information
              </h2>
              <p>
                We use the information we collect for various business purposes, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Providing, operating, maintaining, and optimizing our AI integration and warehouse automation services.</li>
                <li>Responding to inquiries, delivering custom quotes, and providing dedicated customer support.</li>
                <li>Developing and improving our solutions, machine learning models, and website usability.</li>
                <li>Ensuring security, avoiding fraud, and preventing unauthorized access or malware.</li>
                <li>Fulfilling legal and regulatory requirements applicable to our business operations in Qatar and other jurisdictions we serve.</li>
              </ul>
            </div>

            <div id="information-sharing" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                4. Information Sharing
              </h2>
              <p>
                We do not sell or lease your personal information to third parties. We may share information under the following limited circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>
                  <strong className="text-black">Service Partners:</strong> We share necessary operational detail with trusted partners like <span className="font-semibold text-gold">Sradha Technologies</span> to deliver high-quality AI developments and technical integrations.
                </li>
                <li>
                  <strong className="text-black">Service Providers:</strong> Cloud hosting providers, security analysts, and IT support services under strict data privacy obligations.
                </li>
                <li>
                  <strong className="text-black">Legal Compliance:</strong> If required by law, subpoena, or government order to protect our rights, users, or the general public.
                </li>
              </ul>
            </div>

            <div id="data-security" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                5. Data Security
              </h2>
              <p>
                We apply robust technical and organizational security measures to protect your personal data from unauthorized access, alteration, disclosure, or destruction. However, no electronic transmission or digital storage method is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
              </p>
            </div>

            <div id="user-rights" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                6. Your Rights & Choices
              </h2>
              <p>
                Depending on your location and jurisdiction, you may have specific data protection rights, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>The right to access and receive copies of the personal data we hold about you.</li>
                <li>The right to request rectification or correction of inaccurate personal data.</li>
                <li>The right to request the deletion or erasure of your personal data under certain conditions.</li>
                <li>The right to object to or restrict processing of your personal information.</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at <a href="mailto:info@almeone.com" className="text-gold font-semibold hover:underline">info@almeone.com</a>.
              </p>
            </div>

            <div id="cookies" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                7. Cookies & Tracking
              </h2>
              <p>
                We use cookies and similar tracking technologies to track the activity on our website and hold certain information. Cookies are small files placed on your device to remember preferences and analyze traffic patterns.
              </p>
              <p>
                You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website. For more details, please review our <a href="/cookies" className="text-gold font-semibold hover:underline">Cookies Policy</a>.
              </p>
            </div>

            <div id="changes" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                8. Changes to This Policy
              </h2>
              <p>
                We may update our Privacy Policy from time to time to reflect operational, legal, or regulatory changes. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date at the top. We encourage you to review this Privacy Policy periodically for any changes.
              </p>
            </div>

            <div id="contact" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                9. Contact Us
              </h2>
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data handling practices, please reach out to us:
              </p>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 space-y-2 mt-4 max-w-md">
                <p><strong className="text-black">Email:</strong> <a href="mailto:info@almeone.com" className="text-gold hover:underline">info@almeone.com</a></p>
                <p><strong className="text-black">Phone:</strong> +974 33920094</p>
                <p><strong className="text-black">Address:</strong> Doha, Qatar</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
